import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";

const Row = ({ label, value, ok, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-line py-3 last:border-0" data-testid={testId}>
    <dt className="text-sm text-stone">{label}</dt>
    <dd className="flex items-center gap-2 text-right text-sm text-ink">
      {ok === undefined ? null : ok
        ? <CheckCircle2 className="h-4 w-4 shrink-0 text-lime-dark" strokeWidth={1.5} />
        : <AlertCircle className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} />}
      <span className="num break-all">{value}</span>
    </dd>
  </div>
);

/** Read-only Square connection readiness, so going live can be checked at a glance. */
export default function PaymentsStatus() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = () => api.get("/payments/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    load();
    window.addEventListener("billing-data-cleared", load);
    return () => window.removeEventListener("billing-data-cleared", load);
  }, []);

  if (!status) return null;
  const live = status.environment === "production";

  return (
    <div className="card-surface p-7" data-testid="payments-status">
      <SectionTitle>Payment Connection</SectionTitle>
      <p className="-mt-3 mb-5 max-w-2xl text-sm text-stone">
        {live
          ? "This environment is taking real payments. Every charge here moves real money."
          : "This environment is in Square's test mode. No real money moves, and test cards are safe to use."}
      </p>
      <dl>
        <Row testId="payments-status-env" label="Mode"
          value={live ? "Live (production)" : "Test (sandbox)"} ok={status.configured} />
        <Row testId="payments-status-token" label="Access token"
          value={status.access_token_set ? "Connected" : "Missing"} ok={status.access_token_set} />
        <Row testId="payments-status-location" label="Location" value={status.location_id || "Missing"}
          ok={Boolean(status.location_id)} />
        <Row testId="payments-status-webhook-key" label="Webhook signature key"
          value={status.webhook_signature_key_set ? "Registered" : "Missing"}
          ok={status.webhook_signature_key_set} />
        <Row testId="payments-status-webhook-url" label="Webhook address" value={status.webhook_url || "Not set"} />
        <Row testId="payments-status-last-webhook" label="Last update from Square"
          value={status.last_webhook ? `${status.last_webhook.type} · ${timeAgo(status.last_webhook.received_at)}` : "None yet"}
          ok={Boolean(status.last_webhook)} />
        <Row testId="payments-status-count" label="Completed payments recorded" value={status.payments_recorded} />
      </dl>
    </div>
  );
}
